import { Injectable } from '@angular/core';
import { AuthService } from '@libs/shared/auth';
import {
  ActiveCartIdResponse,
  CartItemDeletePayloadModel,
  CartItemModel,
  CartItemPayloadModel,
  CartModel,
  ConfigurableBundleAddToCartModel,
  ShopCollectionResponseModel,
} from '@libs/shop/models';
import { Store } from '@ngrx/store';
import { catchError, defer, filter, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { ConnectorService } from '../connector';
import { CartQueueActionData, CartQueueActionType, CartQueueItem } from '../state';
import { CartStateActions } from '../state/cart-state/actions';
import { CartStateSelectors } from '../state/cart-state/selectors';

@Injectable({ providedIn: 'root' })
export class CartRepositoryService {
  protected readonly cart$: Observable<CartModel | undefined> = this.initCart();
  private readonly cartId$: Observable<string> = this.initCartId();

  public constructor(
    private readonly authService: AuthService,
    private readonly connector: ConnectorService,
    private readonly store: Store,
  ) {
  }

  public getCartId(): Observable<string> {
    return this.cartId$;
  }

  public getCart(): Observable<CartModel | undefined> {
    return this.cart$;
  }

  public setCart(cart: CartModel): void {
    this.store.dispatch(CartStateActions.setCart({ cartData: cart }));
  }

  public setCartId(id: string): void {
    this.store.dispatch(CartStateActions.setCartId({ id: id }));
  }

  public resetCartId(): void {
    this.store.dispatch(CartStateActions.setCartId({ id: '' }));
  }

  public getCartItemsCount(): Observable<number> {
    return this.getCart().pipe(map(cart => cart?.items.length ?? 0));
  }

  public queueCartItem(actionType: CartQueueActionType, data: CartQueueActionData): void {
    this.store.dispatch(
      CartStateActions.addToCartQueue({
        payload: {
          id: data.sku + actionType, // Use a unique queue item ID (not SKU) to support multiple concurrent actions on one cart item
          actionType,
          data,
        },
      }),
    );
  }

  public getCartQueue(): Observable<CartQueueItem[]> {
    return this.store.select(CartStateSelectors.selectCartQueue);
  }

  public deleteAllItemsFromCart(): Observable<CartModel> {
    return this.getCartId().pipe(
      take(1),
      switchMap((cartId: string): Observable<CartModel> => this.connector.connect(
        'shop',
        'cartDeleteAllItems',
        {
          pathParams: {
            cartId,
          },
        },
      )),
      tap((cart: CartModel) => {
        void this.store.dispatch(CartStateActions.setCartId({ id: cart.id }));
        void this.store.dispatch(CartStateActions.setCart({ cartData: cart }));
      }),
    );
  }

  public addItem(sku: string, quantity: number): Observable<CartModel> {
    const payload: CartItemPayloadModel = {
      type: 'cart-item',
      id: sku,
      quantity: quantity,
    };

    return this.getCartId().pipe(
      take(1),
      switchMap((cartId: string): Observable<CartModel> => this.connector.connect(
        'shop',
        'cartItemAdd',
        {
          pathParams: {
            cartId,
          },
          payload,
        },
      )),
      tap((cart: CartModel) => void this.store.dispatch(CartStateActions.setCart({ cartData: cart }))),
    );
  }

  public addItemsCollection(collection: CartItemPayloadModel[]): Observable<ShopCollectionResponseModel<CartModel>> {
    return this.getCartId().pipe(
      take(1),
      switchMap((cartId: string): Observable<ShopCollectionResponseModel<CartModel>> => this.connector.connect(
        'shop',
        'cartItemsAddCollection',
        {
          pathParams: {
            cartId,
          },
          payload: collection,
        },
      )),
      tap((result: ShopCollectionResponseModel<CartModel>) => void this.store.dispatch(CartStateActions.setCart({ cartData: result.value }))),
    );
  }

  public deleteItemsCollection(payload: CartItemDeletePayloadModel[]): Observable<ShopCollectionResponseModel<CartModel>> {
    return this.getCartId().pipe(
      take(1),
      switchMap((cartId: string): Observable<ShopCollectionResponseModel<CartModel>> => this.connector.connect(
        'shop',
        'cartItemsDeleteCollection',
        {
          pathParams: {
            cartId,
          },
          payload,
        },
      )),
      tap((result: ShopCollectionResponseModel<CartModel>) => void this.store.dispatch(CartStateActions.setCart({ cartData: result.value }))),
    );
  }

  public updateItem(item: CartItemModel): Observable<CartModel> {
    const payload: CartItemPayloadModel = {
      type: 'cart-item',
      id: item.id,
      quantity: item.quantity,
    };

    return this.getCartId().pipe(
      take(1),
      switchMap((cartId: string): Observable<CartModel> => this.connector.connect(
        'shop',
        'cartItemUpdate',
        {
          pathParams: {
            cartId,
            itemId: payload.id,
          },
          payload,
        },
      )),
      tap((cart: CartModel) => void this.store.dispatch(CartStateActions.setCart({ cartData: cart }))),
    );
  }

  public removeItem(item: CartItemModel): Observable<CartModel | undefined> {
    return this.getCartId().pipe(
      take(1),
      switchMap((cartId: string): Observable<void> => this.connector.connect(
        'shop',
        'cartItemDelete',
        {
          pathParams: {
            cartId,
            itemId: item.id,
          },
        },
      )),
      switchMap(() => this.initCart()),
      take(1),
      tap((cart: CartModel | undefined) => void this.store.dispatch(CartStateActions.setCart({ cartData: cart }))),
    );
  }

  public addConfigurableBundle(payload: ConfigurableBundleAddToCartModel): Observable<CartModel> {
    return this.getCartId().pipe(
      take(1),
      switchMap((cartId: string): Observable<CartModel> => this.connector.connect(
        'shop',
        'configurableBundleAddToCart',
        {
          pathParams: {
            cartId,
          },
          payload,
        },
      )),
      tap((cart: CartModel) => void this.store.dispatch(CartStateActions.setCart({ cartData: cart }))),
    );
  }

  public requestCart(): Observable<CartModel | undefined> {
    return this.getCartId().pipe(
      switchMap((cartId: string): Observable<CartModel | undefined> => {
        if (!cartId) {
          return of(undefined);
        }

        return this.connector.connect<CartModel>('shop', 'cartGet', {
          pathParams: { cartId },
        });
      }));
  }

  public initCartId(): Observable<string> {
    return defer(() => {
      return this.authService.getCurrentCustomer().pipe(
        filter(c => !!c),
        switchMap(() => this.connector.connect<ActiveCartIdResponse>('shop', 'cartGetActiveCartId').pipe(
          tap(response => {
            this.store.dispatch(CartStateActions.loadCartIdSuccess({
              id: response.id,
            }));
          }),
          catchError((error: Error) => {
            this.store.dispatch(CartStateActions.loadCartIdFailure({ error: error }));

            return of(undefined);
          }),
        )),
      );
    }).pipe(
      switchMap(() => this.store.select(CartStateSelectors.selectCartId)),
      shareReplay(1),
    );
  }

  public addVoucherToCart(voucher: string): Observable<CartModel> {
    return this.getCartId().pipe(
      take(1),
      switchMap((cartId: string): Observable<CartModel> => this.connector.connect(
        'shop',
        'addVoucherToCart',
        {
          pathParams: {
            cartId,
          },
          payload: {
            code: voucher,
            type: 'vouchers',
          },
        },
      )),
      tap((cart: CartModel) => void this.store.dispatch(CartStateActions.setCart({ cartData: cart }))),
    );
  }

  public removeVoucherFromCart(voucherCode: string): Observable<void> {
    return this.getCartId().pipe(
      take(1),
      switchMap((cartId: string): Observable<void> => this.connector.connect(
        'shop',
        'removeVoucherFromCart',
        {
          pathParams: {
            cartId,
            voucherCode,
          },
        },
      )),
    );
  }

  private initCart(): Observable<CartModel | undefined> {
    return defer(() => {
      return this.requestCart().pipe(
        tap((cart?: CartModel): void => {
          this.store.dispatch(CartStateActions.loadCartSuccess({
            cartData: cart,
          }));
        }),
        catchError((error: Error) => {
          this.store.dispatch(CartStateActions.loadCartFailure({ error: error }));

          return of(undefined);
        }));
    }).pipe(
      switchMap(() => this.store.select(CartStateSelectors.selectCart)),
      shareReplay(1),
    );
  }
}
